import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'
import { of } from 'rxjs'
import { catchError, concatMap, distinctUntilChanged, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'

import { Router } from '@angular/router'
import { CANCEL_REQUEST, cancelEditingRequest } from '../../shared/actions/shared.actions'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { PayloadedAction } from '../../shared/types'
import { isDeepCopy } from '../../shared/utils/isDeepCopy'
import { AAAStore } from '../../store/root-reducer'
import {
  selectAgentSettings,
  selectAuthClub,
  selectDefaultVehicle,
  selectEligibility,
  selectIsRapUser,
  selectIsVehicleChangeAllowed
} from '../auth/auth.selectors'
import { selectLocationClub } from '../location/location.selectors'
import { RouteTypes } from '../main.routes'
import { selectHasBatteryQuotes } from '../quotes/quotes.selectors'
import { selectNumberOfPassengers } from '../submit/submit.selectors'
import events from '../tagging/events'
import { TaggingService } from '../tagging/tagging.service'
import {
  openCancelFeedbackModal,
  openMessageDialog,
  openPromptDialog,
  removeVehicleStep
} from '../ui/ui.actions'
import { MessageDialogTypes, PromptDialogTypes, StepTypes } from '../ui/ui.types'
import { callStatusRequest } from './calls-statuses/call-status.actions'
import {
  selectActiveCallStatus,
  selectIsActiveBatteryCall,
  selectIsCallPendingOfService
} from './calls-statuses/call-status.selectors'
import { CallStatusService } from './calls-statuses/call-status.service'
import {
  ADD_CALL,
  ADD_PARTIAL_CALL,
  addCallRequest,
  addCallSuccess,
  addPartialCallSuccess,
  CALL_CANCEL,
  callCancelRequest,
  callCancelSuccess,
  notifyCallCancelFailure,
  notifyPartialCallFailure,
  resetActiveCallsCalled,
  resetCallStatusError,
  setCallTowing,
  submitCancelReason
} from './calls.actions'
import { selectCallData } from './calls.selectors'
import { CallsService } from './calls.service'
import { AAACallData, AAACallStatus, CallResponse, CallStatus, ContractType } from './calls.types'

import { DRR_BASE_HREF } from '../../shared/shared.config'
import { selectSelectedAppointment } from '../appointment/appointment.selectors'
import { Appointment } from '../appointment/appointment.types'
import { ContractIdentityOem, Eligibility } from '../auth/eligibility.types'
import { assignDefaultVehicle, assignEligibilityVehicle } from '../member/member.actions'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { selectChannel } from '../ui/ui.selectors'
import { UIService } from '../ui/ui.service'
import { isAgentAppId } from '../ui/ui.utils'
import { overrideCadillacCelestiqPacesetterCode } from './calls.utils'

@Injectable()
export class CallEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private _callService: CallsService,
    private _callStatusService: CallStatusService,
    private errorReportingService: ErrorReportingService,
    private router: Router,
    private tagging: TaggingService,
    private adobeEventService: AdobeEventService,
    private uiService: UIService,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {}

  addCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof addCallRequest>>(ADD_CALL.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectAgentSettings)),
        this.store$.pipe(select(selectCallData)),
        this.store$.pipe(select(selectNumberOfPassengers)),
        this.store$.pipe(select(selectChannel)),
        this.store$.pipe(select(selectIsRapUser)),
        this.store$.pipe(select(selectEligibility))
      ),
      map(([_, agentSettings, callData, numberOfPassengers, channel, isRapUser, eligibility]) => {
        if(channel) {
          callData.channel = channel
        }


        if (isRapUser) {
          overrideCadillacCelestiqPacesetterCode(
            (eligibility.contractIdentity as ContractIdentityOem).receivingClubCode, callData
          )
          const { verificationId, contractType, contractIdentity }: Eligibility = eligibility
          callData.eligibility = {
            verificationId,
            contractType: contractType as ContractType,
            contractIdentity,
            ...({ receivingClubCode: 'receivingClubCode' in contractIdentity ? contractIdentity.receivingClubCode : null })
          }
        }

        if (numberOfPassengers) {
          callData.numberOfPassengers = numberOfPassengers
        }
        const { agentFirstName, agentLastName } = agentSettings || {}
        if (isAgentAppId()) {
          return {
            ...callData,
            agentFirstName: agentFirstName || '',
            agentLastName: agentLastName || '',
          }
        }
        return callData
      }),
      switchMap((callData: AAACallData) =>
        this._callService.addCall$(callData).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectAuthClub)),
            this.store$.pipe(select(selectLocationClub)),
            this.store$.pipe(select(selectSelectedAppointment))
          ),
          filter((call) => Boolean(call)),
          concatMap(([call, authClub, servicingClub, appointmentScheduled]: [CallResponse, string, string, Appointment]) => {
            this.router.navigate([this.drrBaseHref, RouteTypes.DASHBOARD])
            // TODO improve function call signature

            const analyticsDirectAttributes = {
              ers_call_id: call.callKey,
              servicing_club: servicingClub,
              club: authClub
            }
            this.adobeEventService.sendEvent({
              eventName: AdobeEventTypes.SYSTEM,
              eventValue: events.submit.SUMMARY_CALL_REQUEST_SUCCESS
            },
            null,
            analyticsDirectAttributes
            )
            if (appointmentScheduled) {
              this.adobeEventService.sendEvent({
                eventName: AdobeEventTypes.SYSTEM,
                eventValue: events.submit.APPOINTMENT_SCHEDULED_SUCCESS
              },
              null,
              analyticsDirectAttributes
              )
            }

            this.tagging.setAutomatedEvent(
              events.submit.SUBMIT_SUCCESS,
              events.submit.SUBMIT_PAGE_TYPE, {
                callIdentifier: call.callKey,
                callStatus: CallStatus.REQUEST_RECEIVED,
              },
              {
                callId: call.callKey,
                servicingClub: servicingClub,
              }
            )
            this._callStatusService.startCallStatusesUpdater({ retry: true })

            return [
              addCallSuccess({
                payload: {
                  response: {
                    ...call,
                    callStatus: CallStatus.REQUEST_RECEIVED,
                  },
                  data: callData,
                },
              }),
            ]
          }),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              [
                resetActiveCallsCalled(),
                callStatusRequest({
                  payload: { retry: false, addCallFailure: true },
                })
              ]
            ))
        )
      ),
      catchError((error) =>
        this.errorReportingService.notifyErrorObservable(
          error,
          [
            resetActiveCallsCalled(),
            callStatusRequest({
              payload: { retry: false, addCallFailure: true },
            })
          ]
        )
      )
    )
  )

  addPartialCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_PARTIAL_CALL.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectCallData)),
        this.store$.pipe(select(selectNumberOfPassengers)),
        this.store$.pipe(select(selectIsRapUser)),
        this.store$.pipe(select(selectEligibility))
      ),
      map(([_, callData, numberOfPassengers, isRapUser, eligibility]: [Action, AAACallData, number, boolean, Eligibility]) => {

        if (isRapUser) {
          overrideCadillacCelestiqPacesetterCode(
            (eligibility.contractIdentity as ContractIdentityOem).receivingClubCode, callData
          )
          const { verificationId, contractType, contractIdentity }: Eligibility = eligibility
          callData.eligibility = {
            verificationId: verificationId,
            contractType: contractType as ContractType,
            ...({ receivingClubCode: 'receivingClubCode' in contractIdentity ? contractIdentity.receivingClubCode : null })
          }
        }

        if (numberOfPassengers >= 0) {
          callData.numberOfPassengers = numberOfPassengers
        }

        return callData
      }),
      distinctUntilChanged((prev: AAACallData, curr: AAACallData) => isDeepCopy(prev, curr)),
      switchMap((callData: AAACallData) =>
        this._callService.addPartialCall$(callData).pipe(
          map(() => addPartialCallSuccess()),
          catchError((error) => this.errorReportingService.notifyErrorObservable(
            error,
            notifyPartialCallFailure
          ))
        )
      )
    )
  )

  cancelRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CANCEL_REQUEST),
      switchMap((action) =>
        of(action).pipe(
          tap(() => {
            // reset call status error
            this.store$.dispatch(resetCallStatusError())
          }),
          withLatestFrom(
            this.store$.pipe(select(selectIsCallPendingOfService))
          ),
          switchMap(([_, pendingOfService]) => {
            if (pendingOfService) {
              return [
                openCancelFeedbackModal({
                  payload: {
                    hasAvailableCall: true
                  }
                })
              ];
            } else {
              return [cancelEditingRequest()];
            }
          })
        )
      )
    )
  )

  submitCancelReason$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitCancelReason),
      withLatestFrom(this.store$.select(selectActiveCallStatus)),
      switchMap(([{ payload: reason }, activeCallStatus]) => [
        cancelEditingRequest(),
        callCancelRequest({
          payload: {
            callKey: activeCallStatus.callKey,
            reason
          }
        })
      ]),
      catchError((error) =>
        this.errorReportingService.notifyErrorObservable(
          error,
          notifyCallCancelFailure
        )
      )
    )
  )

  cancelCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CALL_CANCEL.REQUEST),
      switchMap((action: PayloadedAction) =>
        this._callService.cancelCall$(action.payload).pipe(
          switchMap(() => {
            this.tagging.setAutomatedEvent(
              events.dashboard.CALL_CANCEL_SUCCESS,
              events.dashboard.CALL_STATUS_PAGE_TYPE
            )
            this.adobeEventService.sendEvent({
              eventName: AdobeEventTypes.SYSTEM,
              eventValue: events.dashboard.CANCEL_CALL_SUCCESS
            })
            this._callStatusService.stopCallStatusesUpdater()

            return [
              callCancelSuccess({
                payload: action.payload,
              }),
              resetCallStatusError(),
            ]
          }),
          catchError((error) =>
            this._automatedEventAndNotifyFailure(
              error,
              events.dashboard.CALL_CANCEL_FAILURE,
              notifyCallCancelFailure
            )
          )
        )
      )
    )
  )

  cancelCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CALL_CANCEL.SUCCESS),
      withLatestFrom(
        this.store$.pipe(select(selectIsVehicleChangeAllowed)),
        this.store$.pipe(select(selectIsRapUser)),
        this.store$.pipe(select(selectDefaultVehicle))
      ),
      switchMap(([_, isVehicleChangeAllowed, isRapUser, defaultVehicle]) => {
        this.uiService.setModalsClose(false)
        this.router.navigate([this.drrBaseHref, RouteTypes.STEPS], {
          queryParams: { step: StepTypes.BREAKDOWN_LOCATION, cancel: 'true' },
        })
        const actions = []
        if (isRapUser) {
          actions.push(openMessageDialog({
            payload: {
              type: MessageDialogTypes.CALL_CANCELLED,
            }
          }))
        }

        if (!isVehicleChangeAllowed) {
          actions.push(removeVehicleStep())
          actions.push(
            defaultVehicle ? assignDefaultVehicle() : assignEligibilityVehicle()
          )
        }

        return actions
      })
    )
  )

  updateCallTowDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_CALL.SUCCESS),
      withLatestFrom(this.store$.pipe(select(selectActiveCallStatus))),
      filter(([_, activeCallStatus]: [Action, AAACallStatus]) => !!activeCallStatus),
      map(([_, activeCallStatus]: [Action, AAACallStatus]) =>
        setCallTowing({
          payload: activeCallStatus.towDestination
            ? {
              ...activeCallStatus.towDestination,
              name: activeCallStatus.towDestination.location,
              latitude: activeCallStatus.towDestination.latitude,
              longitude: activeCallStatus.towDestination.longitude,
            }
            : null,
        })
      )
    )
  )

  // Show welcome once for each call submitted.
  handleShowWelcome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_CALL.SUCCESS),
      switchMap(() =>
        this.store$.select(selectIsActiveBatteryCall).pipe(
          filter(isActiveBatteryCall => isActiveBatteryCall !== null),
          withLatestFrom(
            this.store$.select(selectHasBatteryQuotes),
            this.store$.select(selectIsRapUser)
          )
        )
      ),
      switchMap(([isActiveBatteryCall, hasBatteryQuotes, isRapUser]) => {
        const textDialog = isRapUser
          ? $localize`Please be with the vehicle when assistance arrives.`
          : $localize`The AAA member is required to be with the vehicle when assistance arrives.<br> Please be prepared to show Photo ID.`;

        const actions = []
        if (isActiveBatteryCall && !hasBatteryQuotes) {
          actions.push(openPromptDialog({
            payload: {
              type: PromptDialogTypes.SHOW_BATTERY_QUOTES,
            },
          }))
        } else {
          actions.push(openMessageDialog({
            payload: {
              type: MessageDialogTypes.ON_OUR_WAY,
              title: $localize`On Our Way!`,
              content: textDialog,
              scrollToTopOnClose: true
            },
          }))
        }
        actions.push(callStatusRequest({ payload: { retry: true } }));
        return actions
      })
    )
  )

  _automatedEventAndNotifyFailure = (
    error,
    eventAction,
    notifyAction
  ) => {
    const observableAction = this.errorReportingService.notifyErrorObservable(
      error,
      notifyAction
    )
    try {
      this.tagging.setAutomatedEvent(eventAction, events.submit.SUBMIT_PAGE_TYPE)
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.SYSTEM,
        eventValue: events.dashboard.CANCEL_CALL_FAILED
      })
    } catch (e) {
      console.error('Failure tagging automated event: ', e)
    }
    return observableAction
  }
}
